import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import type {
  CodeActivityDataPoint,
  LeaderboardEntry,
  LeaderboardResponse,
  ProgressionDataPoint,
  RadarDataPoint,
} from '@/sections/customerManagement/types';
import {
  buildCodeActivityData,
  buildProgressionData,
  buildRadarData,
  computeLeaderboardEntries,
  getActiveMetrics,
} from '@/sections/customerManagement/utils/leaderboardTransforms';
import { useAuth } from '@/state/auth';
import { mergeStatus, type QueryStatus } from '@/utils/api';

function getLast7Days(): string[] {
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days.reverse();
}

export function useMyLeaderboardStats(): {
  progression: ProgressionDataPoint[];
  codeActivity: CodeActivityDataPoint[];
  radarData: RadarDataPoint[];
  currentEntry: LeaderboardEntry | null;
  status: QueryStatus;
} {
  const { me } = useAuth();
  const axios = useAxios({ doNotImpersonate: true });
  const days = getLast7Days();

  const queries = useQueries({
    queries: days.map(date => ({
      queryKey: ['leaderboard-snapshots', date],
      queryFn: async () => {
        const { data } = await axios.get<LeaderboardResponse>('/leaderboard', {
          params: { date },
        });
        return data;
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const status = mergeStatus(...queries.map(q => q.status));

  const { currentEntry, radarData, progression, codeActivity } = useMemo(() => {
    if (status !== 'success') {
      return {
        currentEntry: null,
        radarData: [] as RadarDataPoint[],
        progression: [] as ProgressionDataPoint[],
        codeActivity: [] as CodeActivityDataPoint[],
      };
    }

    let latestIdx = queries.length - 1;
    while (
      latestIdx >= 0 &&
      (!queries[latestIdx]?.data?.snapshots ||
        queries[latestIdx].data!.snapshots.length === 0)
    ) {
      latestIdx--;
    }

    const todaySnapshots =
      latestIdx >= 0 ? (queries[latestIdx].data?.snapshots ?? []) : [];
    let prevIdx = latestIdx - 1;
    while (
      prevIdx >= 0 &&
      (!queries[prevIdx]?.data?.snapshots ||
        queries[prevIdx].data!.snapshots.length === 0)
    ) {
      prevIdx--;
    }
    const yesterdaySnapshots =
      prevIdx >= 0 ? (queries[prevIdx].data?.snapshots ?? []) : [];

    const allEntries = computeLeaderboardEntries(
      todaySnapshots,
      yesterdaySnapshots
    );
    const activeMetrics = getActiveMetrics(todaySnapshots);

    const userEntry = allEntries.find(e => e.email === me) ?? null;

    const radar = userEntry
      ? buildRadarData(userEntry, allEntries, activeMetrics)
      : [];

    const snapshotsByDay = days
      .map((date, i) => ({
        date,
        snapshots: queries[i]?.data?.snapshots ?? [],
      }))
      .filter(d => d.snapshots.length > 0);

    const prog = me ? buildProgressionData(snapshotsByDay, me) : [];
    const activity = me ? buildCodeActivityData(snapshotsByDay, me) : [];

    return {
      currentEntry: userEntry,
      radarData: radar,
      progression: prog,
      codeActivity: activity,
    };
  }, [status, queries, days, me]);

  return { progression, codeActivity, radarData, currentEntry, status };
}
